import React, { useState, useEffect, useContext } from 'react';
import { navigate } from 'gatsby';
import {
  Button,
  Dialog,
} from '@material-ui/core';

import SaveTwoToneIcon from '@material-ui/icons/SaveTwoTone';
import ReplayTwoToneIcon from '@material-ui/icons/ReplayTwoTone';

import { MyCardRow, MyCardSub } from 'components/Elements/MyCard';
import MyInput from 'components/Elements/MyInput';
import TagSelect, { tagsToOptions } from 'components/Elements/TagSelect';

import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { NotificationContext } from 'components/NotificationOverlay';

import { notNull, notEmpty, cleanPhone, formatPhone, isValidEmail } from 'utils/text';
import { logout } from 'services/loginService';
import { passengersService } from 'services/passengersService';
import { tagsService } from 'services/tagsService';
import { DialogTitleContainer, DialogContentContainer, DialogActionsContainer } from './style';

const EditModal = ({
  passengerDetail,
  updatePassengerDetail,
  editOpen,
  handleEditClose,
}) => {
  const [values, setValues] = useState({
    phone: passengerDetail.phone,
    first_name: passengerDetail.first_name,
    last_name: passengerDetail.last_name,
    email: passengerDetail.email,
    passenger_id: passengerDetail.passenger_id,
  });
  const [fieldErrors, setFieldErrors] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { showNotification } = useContext(NotificationContext);

  useEffect(() => {
    setValues({
      ...passengerDetail
    });
  }, [passengerDetail]);

  useEffect(() => {
    if (editOpen) {
      tagsService.get_tags().then(({ data }) => {
        if (data.status === 'SUCCESS') setAllTags(data.data);
      });
    }
  }, [editOpen]);

  const checkFieldValidate = (id, value) => {
    let valid = notNull(value) && (value.length > 0 || value >= 0);
    let error = null;
    if (id === 'email' && notEmpty(value)) {
      error = isValidEmail(value) ? null : 'Please input valid Email';
    } else if (!valid) {
      error = 'Please input value';
    }
    return error;
  };

  const checkDetailValidate = () => {
    const keys = ['first_name', 'last_name', 'email'];
    const errors = {};
    keys.forEach(item => {
      const error = checkFieldValidate(item, notNull(values) ? values[item] : null);
      errors[item] = error;
    })
    setFieldErrors(errors);

    const stepValid = Object.keys(errors).filter(item => errors[item] !== null).length;
    return stepValid === 0;
  };

  const handleDetailChange = (id, newValue) => {
    setValues({
      ...values,
      [id]: newValue
    });
  }

  const handleSave = () => {
    if (!checkDetailValidate()) {
      return;
    }

    showCircularProgress('Saving...');
    passengersService.admin_edit({
      id: values.passenger_id,
      fname: values.first_name,
      lname: values.last_name,
      email: values.email,
      phone: cleanPhone(values.phone),
      tags_require: JSON.stringify(tagsToOptions(values.required_tags || [])),
      tags_exclude: JSON.stringify(tagsToOptions(values.excluded_tags || [])),
    }).then(({ data }) => {
      hideCircularProgress();
      if (data.status === 'SUCCESS') {
        updatePassengerDetail(values);
        handleEditClose();
      } else {
        showNotification('Sorry, Failed to save', 'error');
        if (data.data.code === 'NOT_LOGGED_IN') {
          logout();
          navigate('/login');
        }
      }
    });
  }

  return (
    <Dialog
      classes={{ paper: 'modal-content' }}
      fullWidth
      maxWidth="sm"
      open={editOpen}
      onClose={handleEditClose}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitleContainer className="bg-secondary">
        <span>
          Edit Passenger
        </span>
      </DialogTitleContainer>
      <DialogContentContainer className="p-0" dividers>
        <div className="dialog-content--wrapper bg-secondary border-0">
          <div className="card-body px-lg-4 py-lg-3">
            <div className="card-body--column w-100">
              <MyCardSub subTitle="Personal Information" noBorder>
                <MyCardRow>
                  <MyInput
                    label="First Name"
                    id="first_name"
                    values={values}
                    onChange={handleDetailChange}
                    error={notNull(fieldErrors?.first_name)}
                    helperText={fieldErrors?.first_name}
                  />
                  <MyInput
                    label="Last Name"
                    id="last_name"
                    values={values}
                    onChange={handleDetailChange}
                    error={notNull(fieldErrors?.last_name)}
                    helperText={fieldErrors?.last_name}
                  />
                </MyCardRow>
                <MyCardRow>
                  <MyInput
                    label="Mobile Phone *"
                    id="phone"
                    value={formatPhone(values.phone)}
                    disabled
                  />
                  <MyInput
                    label="Email"
                    id="email"
                    values={values}
                    onChange={handleDetailChange}
                    error={notNull(fieldErrors?.email)}
                    helperText={fieldErrors?.email}
                  />
                </MyCardRow>
              </MyCardSub>
              <br/>
              <MyCardSub subTitle="Tags" noBorder>
                <MyCardRow>
                  <TagSelect
                    label="Required tags (vehicle/driver must have)"
                    options={allTags}
                    value={values.required_tags || []}
                    excludeValues={values.excluded_tags || []}
                    onChange={val => handleDetailChange('required_tags', val)}
                  />
                </MyCardRow>
                <MyCardRow>
                  <TagSelect
                    label="Excluded tags (vehicle/driver must NOT have)"
                    options={allTags}
                    value={values.excluded_tags || []}
                    excludeValues={values.required_tags || []}
                    onChange={val => handleDetailChange('excluded_tags', val)}
                  />
                </MyCardRow>
              </MyCardSub>
            </div>
          </div>
        </div>
      </DialogContentContainer>
      <DialogActionsContainer className="bg-secondary p-3">
        <Button
          size="small"
          className="btn-primary mxr-4 px-4 py-2 btn-transition-none"
          onClick={handleSave}
        >
          <span className="btn-wrapper--icon">
            <SaveTwoToneIcon fontSize="small" />
          </span>
          <span className="btn-wrapper--label">Save</span>
        </Button>
        <Button
          size="small"
          className="btn-warning px-4 py-2 btn-transition-none"
          onClick={handleEditClose}
        >
          <span className="btn-wrapper--icon">
            <ReplayTwoToneIcon fontSize="small" />
          </span>
          <span className="btn-wrapper--label">Cancel</span>
        </Button>
      </DialogActionsContainer>
    </Dialog>
  );
};

export default EditModal;
